import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, X } from "lucide-react";

/**
 * AI health context — polls `/api/ai/health` every 60s (and on demand after a
 * 503-from-AI surface) so the whole app knows whether the LLM is reachable.
 * Pages with AI affordances (Re-analyze button, Generate JD, etc.) read this
 * to gray out their buttons when AI is paused; the global banner here just
 * tells every recruiter at a glance + links them to top up credits.
 */
const AIHealthCtx = createContext({ available: true, refresh: () => {} });
export const useAIHealth = () => useContext(AIHealthCtx);

export function AIHealthProvider({ children }) {
    const [state, setState] = useState({ available: true, reason: null, message: null, updated_at: null });
    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/ai/health");
            setState(data || { available: true });
        } catch {
            // 401 if signed out — silently ignore
        }
    }, []);
    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 60_000);
        return () => clearInterval(t);
    }, [refresh]);
    return (
        <AIHealthCtx.Provider value={{ ...state, refresh }}>
            {children}
        </AIHealthCtx.Provider>
    );
}

export default function AIHealthBanner() {
    const { available, message, reason, refresh } = useAIHealth();
    const [dismissed, setDismissed] = useState(false);
    useEffect(() => { if (available) setDismissed(false); }, [available]);
    if (available || dismissed) return null;
    const isBudget = reason === "budget_exhausted";
    return (
        <div
            className="bg-amber-50 border-b border-amber-300 text-amber-900 px-3 sm:px-6 py-2.5 text-[12px] sm:text-[13px]"
            data-testid="ai-health-banner"
        >
            <div className="max-w-7xl mx-auto flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
                <div className="flex-1 min-w-0">
                    <strong className="font-mono uppercase tracking-widest text-[10px] sm:text-[11px] block sm:inline">
                        {isBudget ? "AI credits exhausted" : "AI service paused"}
                    </strong>
                    <span className="sm:ml-2">
                        {message || "AI parsing & scoring are paused. New resumes still upload — you can retry parsing once it's back."}
                    </span>
                    {isBudget && (
                        <span className="block sm:inline sm:ml-2 mt-1 sm:mt-0">
                            <a
                                href="https://app.emergent.sh/profile"
                                target="_blank"
                                rel="noreferrer"
                                className="font-bold underline hover:text-amber-700"
                                data-testid="ai-health-topup-link"
                            >
                                Top up Universal Key →
                            </a>
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => { refresh(); setDismissed(true); }}
                    aria-label="Dismiss"
                    className="text-amber-700 hover:text-amber-900 shrink-0"
                    data-testid="ai-health-dismiss"
                    title="Dismiss (will reappear on next AI failure)"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
